import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Download, Upload, AlertCircle, CheckCircle } from 'lucide-react';
import { logActivity } from '../lib/activity';

export default function DataManagement() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [importStats, setImportStats] = useState<any>(null);

  const exportData = async () => {
    setLoading(true);
    setMessage(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Niet ingelogd');

      const exportData: any = {
        version: '1.0',
        exportDate: new Date().toISOString(),
        data: {}
      };

      const tables = [
        'revenue_entries',
        'hidden_revenue',
        'fixed_expenses'
      ];

      for (const table of tables) {
        const { data, error } = await supabase
          .from(table)
          .select('*')
          .eq('user_id', user.id);

        if (error) throw error;
        exportData.data[table] = data || [];
      }

      console.log('Export data structuur:', {
        version: exportData.version,
        hasData: !!exportData.data,
        tables: Object.keys(exportData.data)
      });

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `taxibase-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const backupSize = new Blob([JSON.stringify(exportData)]).size;
      await supabase
        .from('backups')
        .insert([{
          user_id: user.id,
          backup_data: exportData,
          backup_size: backupSize,
          description: `Automatische backup - ${new Date().toLocaleString('nl-NL')}`
        }]);

      setMessage({ type: 'success', text: 'Data succesvol geëxporteerd en backup opgeslagen' });
      await logActivity('Data geëxporteerd', 'Alle data is geëxporteerd naar een JSON bestand en een backup is opgeslagen op de server');
    } catch (error: any) {
      console.error('Export error:', error);
      setMessage({ type: 'error', text: `Fout bij exporteren: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const importData = async (file: File) => {
    setLoading(true);
    setMessage(null);
    setImportStats(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Niet ingelogd');

      const text = await file.text();
      let importData;

      try {
        importData = JSON.parse(text);
      } catch (e) {
        throw new Error('Bestand is geen geldig JSON formaat');
      }

      if (!importData.version || !importData.data) {
        console.error('Import data structuur:', importData);
        throw new Error('Ongeldig export bestand: versie of data ontbreekt');
      }

      const stats: any = {};

      const tableOrder = ['revenue_entries', 'hidden_revenue', 'fixed_expenses'];

      for (const tableName of tableOrder) {
        if (importData.data[tableName] && importData.data[tableName].length > 0) {
          const records = importData.data[tableName].map((record: any) => {
            const { id, user_id, created_at, updated_at, ...recordData } = record;
            return { ...recordData, user_id: user.id };
          });

          if (tableName === 'revenue_entries' || tableName === 'hidden_revenue') {
            for (const record of records) {
              const { data: existing } = await supabase
                .from(tableName)
                .select('id')
                .eq('user_id', user.id)
                .eq('date', record.date)
                .maybeSingle();

              if (existing) {
                await supabase
                  .from(tableName)
                  .update(record)
                  .eq('id', existing.id);
              } else {
                await supabase
                  .from(tableName)
                  .insert([record]);
              }
            }
          } else {
            const { error } = await supabase
              .from(tableName)
              .insert(records);

            if (error && error.code !== '23505') {
              throw error;
            }
          }

          stats[tableName] = records.length;
        }
      }

      setImportStats(stats);
      setMessage({ type: 'success', text: 'Data succesvol geïmporteerd!' });
      await logActivity('Data geïmporteerd', 'Data is geïmporteerd vanuit een JSON bestand');

      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error: any) {
      console.error('Import error:', error);
      setMessage({ type: 'error', text: `Fout bij importeren: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (confirm('Weet je zeker dat je deze data wilt importeren? Dit zal nieuwe data toevoegen aan je account.')) {
        importData(file);
      }
    }
    e.target.value = '';
  };

  return (
    <div className="bg-gray-800 rounded-lg shadow p-6">
      <div className="flex items-center space-x-2 mb-4">
        <Download className="h-5 w-5 text-gray-300" />
        <h2 className="text-base font-semibold text-white">Data Beheer</h2>
      </div>

      {message && (
        <div className={`p-3 rounded-lg text-sm mb-4 flex items-start space-x-2 ${
          message.type === 'success'
            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
            : 'bg-red-500/10 text-red-400 border border-red-500/20'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          ) : (
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {importStats && (
        <div className="bg-gray-700/50 rounded-lg p-4 mb-4 border border-gray-600">
          <h3 className="text-sm font-semibold text-white mb-2">Import Statistieken:</h3>
          <ul className="text-sm text-gray-300 space-y-1">
            {Object.entries(importStats).map(([key, value]) => (
              <li key={key}>
                <span className="text-gray-400">{key}:</span>{' '}
                <span className="text-emerald-400">{value}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <p className="text-sm text-gray-400 mb-3">
            Exporteer al je data om een backup te maken of om over te zetten naar een ander account.
          </p>
          <button
            type="button"
            onClick={exportData}
            disabled={loading}
            className="w-full flex items-center justify-center px-3.5 py-2.5 rounded-lg text-sm font-medium text-white bg-accent hover:bg-accent-hover transition-colors duration-200 disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Exporteren...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Data Exporteren
              </>
            )}
          </button>
        </div>

        <div className="pt-3 border-t border-gray-700">
          <p className="text-sm text-gray-400 mb-3">
            Importeer data uit een eerder geëxporteerd bestand. Dit voegt nieuwe data toe aan je huidige account.
          </p>
          <label className="w-full flex items-center justify-center px-3.5 py-2.5 rounded-lg text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 hover:text-white transition-colors duration-200 cursor-pointer">
            <Upload className="h-4 w-4 mr-2" />
            Data Importeren
            <input
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              disabled={loading}
              className="hidden"
            />
          </label>
        </div>

        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
          <div className="flex items-start space-x-2">
            <AlertCircle className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-blue-300">
              <p className="font-medium mb-1">Let op:</p>
              <ul className="list-disc list-inside space-y-0.5 text-blue-300/80">
                <li>Export bevat geen documenten (alleen referenties)</li>
                <li>Import voegt data toe aan je bestaande data</li>
                <li>Omzet entries voor dezelfde datum worden bijgewerkt</li>
                <li>Duplicaten worden automatisch overgeslagen</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
